import React from 'react';

import { useDirtyNavigationGuard } from './dirtyNavigation';
import {
  NAVIGATION_PURGE_EVENT,
  buildSessionStateStorageKey,
  createSessionStore,
  type SessionStateAdapter,
  type SessionStateIdentity,
  type SessionStore,
} from './sessionStore';

const SESSION_WRITE_DEBOUNCE_MS = 250;

function advanceSessionRevision(revision: number, increments = 1): number {
  if (!Number.isSafeInteger(revision) || revision < 0
    || !Number.isSafeInteger(increments) || increments < 1) return revision;
  const remaining = Number.MAX_SAFE_INTEGER - revision;
  return increments <= remaining
    ? revision + increments
    : increments - remaining - 1;
}

export interface SessionDraftResult<T> {
  draft: T;
  setDraft: React.Dispatch<React.SetStateAction<T>>;
  restored: boolean;
  dirty: boolean;
  unpersistableChanges: boolean;
  fileReselectionRequired: boolean;
  revision: number;
  markFileSelected(): void;
  clearFileSelection(): void;
  clear(): boolean;
}

export interface SessionDraftOptions<T> {
  onDiscard?: () => void;
  /** Opt-in form ownership: local persistence is not permission to leave. */
  blockNavigationWhen?: (draft: T) => boolean;
  navigationPending?: boolean;
  navigationMessage?: string;
  onNavigationStay?: () => void;
  onNavigationBlocked?: () => void;
}

export interface SessionViewStateResult<T> {
  value: T;
  setValue: React.Dispatch<React.SetStateAction<T>>;
  restored: boolean;
  clear(): void;
}

interface SessionSnapshot<T> {
  key: string;
  identity: SessionStateIdentity;
  adapter: SessionStateAdapter<T>;
  initial: T;
  value: T;
  restored: boolean;
  dirty: boolean;
  fileReselectionRequired: boolean;
  persisted: boolean;
  revision: number;
}

interface PendingTimer {
  handle: ReturnType<typeof setTimeout>;
  key: string;
  revision: number;
  localGeneration: number;
  moduleGeneration: number;
}

interface PreRestoreQueue<T> {
  key: string;
  moduleGeneration: number;
  updates: Array<React.SetStateAction<T>>;
  fileSelection: boolean | null;
  fileSelectionUpdates: number;
  reset: { initial: T } | null;
}

interface SessionCoreResult<T> {
  snapshot: SessionSnapshot<T>;
  setValue: React.Dispatch<React.SetStateAction<T>>;
  markFileSelected(): void;
  clearFileSelection(): void;
  clear(expected?: { key: string; revision: number }): boolean;
}

let navigationSessionGeneration = 0;
let defaultStore: SessionStore | null = null;

function unavailableStorage(): Storage {
  const unavailable = () => {
    throw new Error('sessionStorage is unavailable');
  };
  return {
    get length(): number { return unavailable(); },
    clear: unavailable,
    getItem: unavailable,
    key: unavailable,
    removeItem: unavailable,
    setItem: unavailable,
  };
}

function getDefaultStore(): SessionStore {
  if (defaultStore) return defaultStore;
  let storage: Storage;
  try {
    storage = window.sessionStorage;
  } catch {
    storage = unavailableStorage();
  }
  defaultStore = createSessionStore(storage, () => Date.now());
  return defaultStore;
}

/** Apply a persisted-draft transform only while the submitted snapshot still owns it. */
export function transformStoredSessionDraftIfUnchanged<T>(
  identity: SessionStateIdentity,
  adapter: SessionStateAdapter<T>,
  submittedValue: T,
  transform: (storedValue: T) => T | null,
  submittedFileReselectionRequired = false,
  submittedRevision?: number,
): boolean {
  const store = getDefaultStore();
  const stored = store.read(identity, adapter);
  if (!stored) return true;
  try {
    const storedValue = JSON.stringify(adapter.encode(stored.value));
    const submitted = JSON.stringify(adapter.encode(submittedValue));
    if (storedValue !== submitted
      || stored.fileReselectionRequired !== submittedFileReselectionRequired
      || (submittedRevision !== undefined && stored.revision !== submittedRevision)) return false;
    const transformed = transform(stored.value);
    if (transformed === null) store.remove(identity);
    else store.write(
      identity,
      adapter,
      transformed,
      stored.fileReselectionRequired,
      advanceSessionRevision(stored.revision),
    );
    return true;
  } catch {
    return false;
  }
}

/**
 * Remove only the persisted draft value that a successful stale mutation
 * submitted. A remounted editor that has since changed the same identity wins.
 */
export function clearStoredSessionDraftIfUnchanged<T>(
  identity: SessionStateIdentity,
  adapter: SessionStateAdapter<T>,
  submittedValue: T,
  submittedFileReselectionRequired = false,
  submittedRevision?: number,
): boolean {
  return transformStoredSessionDraftIfUnchanged(
    identity,
    adapter,
    submittedValue,
    () => null,
    submittedFileReselectionRequired,
    submittedRevision,
  );
}

function snapshotIdentity(identity: SessionStateIdentity): SessionStateIdentity {
  return Object.freeze({
    scopeId: identity.scopeId,
    userId: identity.userId,
    workspace: identity.workspace,
    routeId: identity.routeId,
    entityId: identity.entityId ?? null,
    purpose: identity.purpose,
    version: identity.version,
  });
}

function isDirty<T>(
  adapter: SessionStateAdapter<T>,
  value: T,
  initial: T,
): boolean {
  try {
    return adapter.isDirty(value, initial);
  } catch {
    return true;
  }
}

function hasUnpersistableChanges<T>(
  adapter: SessionStateAdapter<T>,
  value: T,
  initial: T,
): boolean {
  if (!adapter.hasUnpersistableChanges) return false;
  try {
    return adapter.hasUnpersistableChanges(value, initial);
  } catch {
    return true;
  }
}

function preparedSnapshot<T>(
  identity: SessionStateIdentity,
  adapter: SessionStateAdapter<T>,
  key: string,
): SessionSnapshot<T> {
  const stableIdentity = snapshotIdentity(identity);
  const initial = adapter.initial();
  return {
    key,
    identity: stableIdentity,
    adapter,
    initial,
    value: initial,
    restored: false,
    dirty: false,
    fileReselectionRequired: false,
    persisted: true,
    revision: 0,
  };
}

function restorePreparedSnapshot<T>(prepared: SessionSnapshot<T>): SessionSnapshot<T> {
  const stored = getDefaultStore().read(prepared.identity, prepared.adapter);
  const value = stored?.value ?? prepared.initial;
  return {
    ...prepared,
    value,
    restored: true,
    dirty: isDirty(prepared.adapter, value, prepared.initial),
    fileReselectionRequired: stored?.fileReselectionRequired ?? false,
    persisted: true,
    revision: stored?.revision ?? prepared.revision,
  };
}

function needsPersistence<T>(snapshot: SessionSnapshot<T>): boolean {
  return snapshot.dirty || snapshot.fileReselectionRequired;
}

function useSessionCore<T>(
  identity: SessionStateIdentity,
  adapter: SessionStateAdapter<T>,
  allowFileMarker: boolean,
  protectOwnership = false,
): SessionCoreResult<T> {
  const key = buildSessionStateStorageKey(identity);
  const initialStateRef = React.useRef<SessionSnapshot<T> | null>(null);
  if (initialStateRef.current === null) {
    initialStateRef.current = preparedSnapshot(identity, adapter, key);
  }
  const [state, setState] = React.useState<SessionSnapshot<T>>(initialStateRef.current);
  const preparedTransitionRef = React.useRef<SessionSnapshot<T> | null>(null);
  let rendered = state;
  if (state.key !== key) {
    if (preparedTransitionRef.current?.key !== key) {
      preparedTransitionRef.current = preparedSnapshot(identity, adapter, key);
    }
    rendered = preparedTransitionRef.current;
  }
  const currentRef = React.useRef(state);
  const renderedSnapshotRef = React.useRef(rendered);
  renderedSnapshotRef.current = rendered;
  const preRestoreQueueRef = React.useRef<PreRestoreQueue<T> | null>(null);
  const timerRef = React.useRef<PendingTimer | null>(null);
  const localGenerationRef = React.useRef(0);
  const mountedRef = React.useRef(false);
  const renderedKeyRef = React.useRef(key);
  renderedKeyRef.current = key;

  const cancelTimer = React.useCallback(() => {
    const timer = timerRef.current;
    if (timer) clearTimeout(timer.handle);
    timerRef.current = null;
  }, []);

  const publish = React.useCallback((next: SessionSnapshot<T>) => {
    currentRef.current = next;
    if (mountedRef.current) setState(next);
  }, []);

  const persistNow = React.useCallback((
    target: SessionSnapshot<T>,
    updateStatus: boolean,
  ): boolean => {
    if (!needsPersistence(target)) {
      getDefaultStore().remove(target.identity);
      if (updateStatus && currentRef.current.key === target.key) {
        publish({ ...currentRef.current, persisted: true });
      }
      return true;
    }
    try {
      if (protectOwnership) {
        const stored = getDefaultStore().read(target.identity, target.adapter);
        if (stored && (stored.revision > target.revision
          || (stored.revision === target.revision
            && (JSON.stringify(target.adapter.encode(stored.value)) !== JSON.stringify(target.adapter.encode(target.value))
              || stored.fileReselectionRequired !== target.fileReselectionRequired)))) return false;
      }
      getDefaultStore().write(
        target.identity,
        target.adapter,
        target.value,
        target.fileReselectionRequired,
        target.revision,
      );
      if (updateStatus
        && currentRef.current.key === target.key
        && currentRef.current.revision === target.revision) {
        publish({ ...currentRef.current, persisted: true });
      }
      return true;
    } catch {
      if (updateStatus
        && currentRef.current.key === target.key
        && currentRef.current.revision === target.revision) {
        publish({ ...currentRef.current, persisted: false });
      }
      return false;
    }
  }, [protectOwnership, publish]);

  const schedule = React.useCallback((target: SessionSnapshot<T>) => {
    cancelTimer();
    const localGeneration = localGenerationRef.current;
    const moduleGeneration = navigationSessionGeneration;
    const pending: PendingTimer = {
      key: target.key,
      revision: target.revision,
      localGeneration,
      moduleGeneration,
      handle: setTimeout(() => {
        if (timerRef.current !== pending) return;
        timerRef.current = null;
        if (navigationSessionGeneration !== moduleGeneration
          || localGenerationRef.current !== localGeneration
          || currentRef.current.key !== target.key
          || currentRef.current.revision !== target.revision) return;
        persistNow(target, true);
      }, SESSION_WRITE_DEBOUNCE_MS),
    };
    timerRef.current = pending;
  }, [cancelTimer, persistNow]);

  const commitPreparedSnapshot = React.useCallback((prepared: SessionSnapshot<T>) => {
    const queuedCandidate = preRestoreQueueRef.current;
    preRestoreQueueRef.current = null;
    const queued = queuedCandidate?.key === prepared.key
      && queuedCandidate.moduleGeneration === navigationSessionGeneration
      ? queuedCandidate
      : null;
    let restored: SessionSnapshot<T>;
    if (queued?.reset) {
      restored = {
        ...prepared,
        initial: queued.reset.initial,
        value: queued.reset.initial,
        restored: true,
        dirty: false,
        fileReselectionRequired: false,
        persisted: true,
        revision: advanceSessionRevision(prepared.revision),
      };
    } else {
      restored = restorePreparedSnapshot(prepared);
    }

    const hasLocalMutation = queued !== null
      && (queued.updates.length > 0 || queued.fileSelectionUpdates > 0);
    let next = restored;
    if (queued && hasLocalMutation) {
      let value = restored.value;
      for (const update of queued.updates) {
        value = typeof update === 'function'
          ? (update as (current: T) => T)(value)
          : update;
      }
      const dirty = isDirty(restored.adapter, value, restored.initial);
      const fileReselectionRequired = queued.fileSelection
        ?? restored.fileReselectionRequired;
      next = {
        ...restored,
        value,
        dirty,
        fileReselectionRequired,
        persisted: !dirty && !fileReselectionRequired,
        revision: advanceSessionRevision(
          restored.revision,
          queued.updates.length + queued.fileSelectionUpdates,
        ),
      };
    }

    currentRef.current = next;
    setState(next);
    if (!hasLocalMutation) return;
    if (needsPersistence(next)) {
      schedule(next);
    } else {
      cancelTimer();
      localGenerationRef.current += 1;
      getDefaultStore().remove(next.identity);
    }
  }, [cancelTimer, schedule]);

  React.useLayoutEffect(() => {
    const previous = currentRef.current;
    if (previous.key === key) {
      if (previous.restored) return;
      commitPreparedSnapshot(previous);
      return;
    }

    const mayFlush = previous.identity.scopeId === identity.scopeId
      && previous.identity.userId === identity.userId;
    cancelTimer();
    localGenerationRef.current += 1;
    if (mayFlush && needsPersistence(previous) && !previous.persisted) {
      persistNow(previous, false);
    }
    const prepared = preparedTransitionRef.current?.key === key
      ? preparedTransitionRef.current
      : preparedSnapshot(identity, adapter, key);
    preparedTransitionRef.current = null;
    commitPreparedSnapshot(prepared);
  }, [adapter, cancelTimer, commitPreparedSnapshot, identity, key, persistNow]);

  React.useEffect(() => {
    mountedRef.current = true;
    const handlePurge = () => {
      preRestoreQueueRef.current = null;
      cancelTimer();
      localGenerationRef.current += 1;
      const active = currentRef.current;
      const initial = active.adapter.initial();
      publish({
        ...active,
        initial,
        value: initial,
        restored: true,
        dirty: false,
        fileReselectionRequired: false,
        persisted: true,
        revision: advanceSessionRevision(active.revision),
      });
    };
    window.addEventListener(NAVIGATION_PURGE_EVENT, handlePurge);
    return () => {
      window.removeEventListener(NAVIGATION_PURGE_EVENT, handlePurge);
      mountedRef.current = false;
      preRestoreQueueRef.current = null;
      cancelTimer();
      const active = currentRef.current;
      if (active.key === renderedKeyRef.current
        && needsPersistence(active)
        && !active.persisted) {
        persistNow(active, false);
      }
    };
  }, [cancelTimer, persistNow, publish]);

  const setValue = React.useCallback<React.Dispatch<React.SetStateAction<T>>>((update) => {
    const renderedKey = renderedKeyRef.current;
    const current = currentRef.current;
    const active = current.key === renderedKey ? current : renderedSnapshotRef.current;
    if (active.key !== renderedKey) return;
    if (!active.restored) {
      const moduleGeneration = navigationSessionGeneration;
      let queued = preRestoreQueueRef.current;
      if (queued?.key !== active.key || queued.moduleGeneration !== moduleGeneration) {
        queued = {
          key: active.key,
          moduleGeneration,
          updates: [],
          fileSelection: null,
          fileSelectionUpdates: 0,
          reset: null,
        };
        preRestoreQueueRef.current = queued;
      }
      queued.updates.push(update);
      return;
    }
    const value = typeof update === 'function'
      ? (update as (current: T) => T)(active.value)
      : update;
    const dirty = isDirty(active.adapter, value, active.initial);
    const next: SessionSnapshot<T> = {
      ...active,
      value,
      dirty,
      persisted: !dirty && !active.fileReselectionRequired,
      revision: advanceSessionRevision(active.revision),
    };
    publish(next);
    if (needsPersistence(next)) {
      schedule(next);
    } else {
      cancelTimer();
      localGenerationRef.current += 1;
      getDefaultStore().remove(next.identity);
    }
  }, [cancelTimer, publish, schedule]);

  const markFileSelected = React.useCallback(() => {
    if (!allowFileMarker) return;
    const renderedKey = renderedKeyRef.current;
    const current = currentRef.current;
    const active = current.key === renderedKey ? current : renderedSnapshotRef.current;
    if (active.key !== renderedKey) return;
    if (!active.restored) {
      const moduleGeneration = navigationSessionGeneration;
      let queued = preRestoreQueueRef.current;
      if (queued?.key !== active.key || queued.moduleGeneration !== moduleGeneration) {
        queued = {
          key: active.key,
          moduleGeneration,
          updates: [],
          fileSelection: null,
          fileSelectionUpdates: 0,
          reset: null,
        };
        preRestoreQueueRef.current = queued;
      }
      queued.fileSelection = true;
      queued.fileSelectionUpdates += 1;
      return;
    }
    const next: SessionSnapshot<T> = {
      ...active,
      fileReselectionRequired: true,
      persisted: false,
      revision: advanceSessionRevision(active.revision),
    };
    publish(next);
    schedule(next);
  }, [allowFileMarker, publish, schedule]);

  const clearFileSelection = React.useCallback(() => {
    if (!allowFileMarker) return;
    const renderedKey = renderedKeyRef.current;
    const current = currentRef.current;
    const active = current.key === renderedKey ? current : renderedSnapshotRef.current;
    if (active.key !== renderedKey) return;
    if (!active.restored) {
      const moduleGeneration = navigationSessionGeneration;
      let queued = preRestoreQueueRef.current;
      if (queued?.key !== active.key || queued.moduleGeneration !== moduleGeneration) {
        queued = {
          key: active.key,
          moduleGeneration,
          updates: [],
          fileSelection: null,
          fileSelectionUpdates: 0,
          reset: null,
        };
        preRestoreQueueRef.current = queued;
      }
      queued.fileSelection = false;
      queued.fileSelectionUpdates += 1;
      return;
    }
    if (!active.fileReselectionRequired) return;
    const next: SessionSnapshot<T> = {
      ...active,
      fileReselectionRequired: false,
      persisted: !active.dirty,
      revision: advanceSessionRevision(active.revision),
    };
    publish(next);
    if (needsPersistence(next)) {
      schedule(next);
    } else {
      cancelTimer();
      localGenerationRef.current += 1;
      getDefaultStore().remove(next.identity);
    }
  }, [allowFileMarker, cancelTimer, publish, schedule]);

  const clear = React.useCallback((expected?: { key: string; revision: number }): boolean => {
    const renderedKey = renderedKeyRef.current;
    const current = currentRef.current;
    const active = current.key === renderedKey ? current : renderedSnapshotRef.current;
    if (active.key !== renderedKey
      || (expected && (active.key !== expected.key || active.revision !== expected.revision))) return false;
    cancelTimer();
    localGenerationRef.current += 1;
    if (expected) {
      const stored = getDefaultStore().read(active.identity, active.adapter);
      if (stored && (stored.revision > active.revision
        || (stored.revision === active.revision
          && (JSON.stringify(active.adapter.encode(stored.value)) !== JSON.stringify(active.adapter.encode(active.value))
            || stored.fileReselectionRequired !== active.fileReselectionRequired)))) return false;
    }
    getDefaultStore().remove(active.identity);
    const initial = active.adapter.initial();
    if (!active.restored) {
      preRestoreQueueRef.current = {
        key: active.key,
        moduleGeneration: navigationSessionGeneration,
        updates: [],
        fileSelection: null,
        fileSelectionUpdates: 0,
        reset: { initial },
      };
      return true;
    }
    preRestoreQueueRef.current = null;
    publish({
      ...active,
      initial,
      value: initial,
      restored: true,
      dirty: false,
      fileReselectionRequired: false,
      persisted: true,
      revision: advanceSessionRevision(active.revision),
    });
    return true;
  }, [cancelTimer, publish]);

  return {
    snapshot: rendered,
    setValue,
    markFileSelected,
    clearFileSelection,
    clear,
  };
}

export function useSessionDraft<T>(
  identity: SessionStateIdentity,
  adapter: SessionStateAdapter<T>,
  options?: SessionDraftOptions<T>,
): SessionDraftResult<T> {
  const session = useSessionCore(identity, adapter, true, options?.blockNavigationWhen !== undefined);
  const unpersistableChanges = hasUnpersistableChanges(
    session.snapshot.adapter,
    session.snapshot.value,
    session.snapshot.initial,
  );
  const unsafeToLeave = (options?.blockNavigationWhen
    ? options.blockNavigationWhen(session.snapshot.value)
    : session.snapshot.dirty && !session.snapshot.persisted)
    || session.snapshot.fileReselectionRequired
    || unpersistableChanges
    || options?.navigationPending === true;
  const protectOwnership = options?.blockNavigationWhen !== undefined;
  const owned = React.useMemo(() => protectOwnership
    ? {key: session.snapshot.key, revision: session.snapshot.revision} : undefined,
  [protectOwnership, session.snapshot.key, session.snapshot.revision]);
  const releaseDirtyNavigation = useDirtyNavigationGuard({
    when: unsafeToLeave,
    preventDiscard: options?.navigationPending,
    message: options?.navigationMessage,
    onStay: options?.onNavigationStay,
    onBlocked: options?.onNavigationBlocked,
    onDiscard: () => {
      if (!session.clear(owned)) throw new Error('The draft is no longer owned by this form.');
      options?.onDiscard?.();
    },
  });
  const clearSession = session.clear;
  const clear = React.useCallback(() => {
    if (owned) {
      if (!clearSession(owned)) return false;
      releaseDirtyNavigation();
      return true;
    }
    releaseDirtyNavigation();
    return clearSession();
  }, [clearSession, owned, releaseDirtyNavigation]);
  return {
    draft: session.snapshot.value,
    setDraft: session.setValue,
    restored: session.snapshot.restored,
    dirty: session.snapshot.dirty,
    unpersistableChanges,
    fileReselectionRequired: session.snapshot.fileReselectionRequired,
    revision: session.snapshot.revision,
    markFileSelected: session.markFileSelected,
    clearFileSelection: session.clearFileSelection,
    clear,
  };
}

export function useSessionViewState<T>(
  identity: SessionStateIdentity,
  adapter: SessionStateAdapter<T>,
): SessionViewStateResult<T> {
  const session = useSessionCore(identity, adapter, false);
  const clearSession = session.clear;
  const clear = React.useCallback(() => { clearSession(); }, [clearSession]);
  return {
    value: session.snapshot.value,
    setValue: session.setValue,
    restored: session.snapshot.restored,
    clear,
  };
}

export function purgeNavigationState(reason: 'logout' | 'scope-change'): void {
  navigationSessionGeneration += 1;
  getDefaultStore().purge();
  window.dispatchEvent(new CustomEvent(NAVIGATION_PURGE_EVENT, {
    detail: { reason },
  }));
}
